import {
  Box,
  Typography,
  Card,
  CardActionArea,
  Stack,
  Link,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import AssessmentIcon from "@mui/icons-material/Assessment";
import EditNoteIcon from "@mui/icons-material/EditNote";
import { useAuth } from "react-oidc-context";
import { jwtDecode } from "jwt-decode";
import { useTranslation } from "i18n-fe";
import { PageLayout, StyledBreadcrumb, useActiveOrg } from "common-fe";

const MANAGER_FE_BASE_URL = import.meta.env.VITE_MANAGER_FE_BASE_URL;
const SCOREADMIN_FE_BASE_URL = import.meta.env.VITE_SCOREADMIN_FE_BASE_URL;
const SCRIBE_FE_BASE_URL = import.meta.env.VITE_SCRIBE_FE_BASE_URL;

// Entitlements are Layer 2 (licensing, AUTHZ.md §2): the access token carries an
// `entitlements` claim — a JSON per-org map { orgId: [appKeys] } stamped by the
// account PreTokenGeneration trigger. We show a launcher card only when the
// active org holds the app's entitlement (super sees everything). Mirrors
// scoreadmin's ScoreEntitlementGate.
const entitlementsFor = (accessToken, orgId) => {
  if (!accessToken || !orgId) return [];
  try {
    const claims = jwtDecode(accessToken);
    const raw = claims.entitlements;
    const map = typeof raw === "string" ? JSON.parse(raw) : raw || {};
    const apps = map[orgId];
    return Array.isArray(apps) ? apps : [];
  } catch {
    return [];
  }
};

export default function Home() {
  const { t } = useTranslation("common");
  const auth = useAuth();
  const { activeOrgId, isSuper } = useActiveOrg();
  const accessToken = auth.user?.access_token;
  const entitlements = entitlementsFor(accessToken, activeOrgId);

  // requires = the entitlement app-key the active org must hold. Super admins
  // bypass the check so they can jump into any surface.
  const has = (requires) => isSuper || entitlements.includes(requires);

  const apps = [
    {
      key: "manager",
      title: t("home.apps.manager.title"),
      description: t("home.apps.manager.description"),
      icon: <DashboardIcon sx={{ fontSize: 56 }} />,
      url: MANAGER_FE_BASE_URL,
      requires: "manager",
    },
    {
      key: "scoreadmin",
      title: t("home.apps.scoreadmin.title"),
      description: t("home.apps.scoreadmin.description"),
      icon: <AssessmentIcon sx={{ fontSize: 56 }} />,
      url: SCOREADMIN_FE_BASE_URL,
      requires: "score",
    },
    {
      key: "scribe",
      title: t("home.apps.scribe.title"),
      description: t("home.apps.scribe.description"),
      icon: <EditNoteIcon sx={{ fontSize: 56 }} />,
      url: SCRIBE_FE_BASE_URL,
      requires: "score",
    },
  ].filter((app) => app.url && has(app.requires));

  const breadcrumbs = [
    <StyledBreadcrumb key="home" label={t("home.appsPageTitle")} />,
  ];

  return (
    <PageLayout maxWidth={1000} breadcrumbs={breadcrumbs}>
      {apps.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 6, textAlign: "center" }}>
          {t("home.noApps")}
        </Typography>
      ) : (
        <Box
          sx={{
            width: "100%",
            display: "grid",
            gap: 3,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(auto-fit, minmax(260px, 1fr))",
            },
          }}
        >
          {apps.map((app) => (
            <Card
              key={app.key}
              sx={{
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": { transform: "translateY(-4px)", boxShadow: 6 },
              }}
            >
              <CardActionArea
                component={Link}
                href={app.url}
                sx={{
                  height: "100%",
                  minHeight: 220,
                  p: 4,
                  textDecoration: "none",
                  "&:hover": { textDecoration: "none" },
                }}
              >
                <Stack spacing={2} alignItems="center" textAlign="center">
                  <Box sx={{ color: "text.primary" }}>{app.icon}</Box>
                  <Typography variant="h5" sx={{ color: "text.primary" }}>
                    {app.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {app.description}
                  </Typography>
                </Stack>
              </CardActionArea>
            </Card>
          ))}
        </Box>
      )}
    </PageLayout>
  );
}
